import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { Branch } from '../branchesMeta';
import { BranchEnrollModal } from './BranchEnrollModal';
import { useBranchEnroll } from './useBranchEnroll';

type BranchEnrollCellProps = {
    branch: Branch;
};

/**
 * Self-served "API token" cell for the branches table: a chip saying whether a
 * token is set — and, once the modal has looked it up, which one (its last
 * four, behind bullets) — plus an Enroll/Manage button opening the enrollment
 * modal. Owns its own enrollment hook + modal so the table View stays
 * render-only.
 *
 * The chip never shows a token. Since BOS-118 the server does not hold one to
 * show: the plaintext is disclosed once, at the moment it is minted.
 */
export const BranchEnrollCell: React.FC<BranchEnrollCellProps> = ({
    branch,
}) => {
    const { t } = useTranslation('pages');
    const enroll = useBranchEnroll(branch);

    return (
        <div
            className="flex items-center justify-center gap-2"
            {...getNodeTestIdProp(`branch-enroll-cell-${branch.id}`)}
        >
            {enroll.hasToken ? (
                <Chip
                    color="success"
                    size="sm"
                    variant="soft"
                    {...getNodeTestIdProp(`branch-token-set-${branch.id}`)}
                >
                    {enroll.tokenPreview !== null
                        ? t('branches.enroll.chipPreview', {
                              preview: enroll.tokenPreview,
                          })
                        : t('branches.enroll.chipSet')}
                </Chip>
            ) : (
                <Chip
                    color="default"
                    size="sm"
                    variant="soft"
                    {...getNodeTestIdProp(`branch-token-unset-${branch.id}`)}
                >
                    {t('branches.enroll.chipUnset')}
                </Chip>
            )}
            <Button
                onPress={enroll.onOpen}
                size="sm"
                variant="tertiary"
                {...getNodeTestIdProp(`branch-enroll-open-${branch.id}`)}
            >
                <Icon icon="gravity-ui:key" />
                {enroll.hasToken
                    ? t('branches.enroll.manage')
                    : t('branches.enroll.enroll')}
            </Button>
            <BranchEnrollModal
                branchName={branch.name}
                copied={enroll.copied}
                error={enroll.error}
                hasToken={enroll.hasToken}
                isGenerating={enroll.isGenerating}
                isLoadingToken={enroll.isLoadingToken}
                isOpen={enroll.isOpen}
                isRevoking={enroll.isRevoking}
                isCopying={enroll.isCopying}
                onClose={enroll.onClose}
                onCopy={enroll.onCopy}
                onCopyActive={enroll.onCopyActive}
                onGenerate={enroll.onGenerate}
                onRevoke={enroll.onRevoke}
                revealedToken={enroll.revealedToken}
                setAt={enroll.setAt}
                tokenPreview={enroll.tokenPreview}
            />
        </div>
    );
};
